import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';
import { API_ORIGIN } from '../config';

const ClientAdminContext = createContext();

export const useClientAdmin = () => {
  const context = useContext(ClientAdminContext);
  if (!context) {
    throw new Error('useClientAdmin must be used within a ClientAdminProvider');
  }
  return context;
};

export const isActive = () => {
  const response = fetch(`${API_ORIGIN}/api/clientadmin/activeForms`, {
    method: 'GET',
  });
  const data = response.json();
  if (response.ok && data.active) {
    return true;
  } else {
    return false;
  }
};

export const ClientAdminProvider = ({ children }) => {
  const [clientAdmin, setClientAdmin] = useState(() => {
    // Initialize from sessionStorage if available
    const stored = sessionStorage.getItem('clientAdmin');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(false);

  // Restore from sessionStorage if context becomes null but session exists
  useEffect(() => {
    if (!clientAdmin) {
      const stored = sessionStorage.getItem('clientAdmin');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setClientAdmin(parsed);
        } catch (error) {
          // ignore
        }
      }
    }
  }, [clientAdmin]);

  // Persist to sessionStorage whenever clientAdmin changes
  // CRITICAL: Only persist, never remove (removal only on explicit logout)
  useEffect(() => {
    if (clientAdmin) {
      sessionStorage.setItem('clientAdmin', JSON.stringify(clientAdmin));
    }
  }, [clientAdmin]);

  // Login function
  const login = async (name, password) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/clientadmin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, password }),
      });

      const data = await response.json();

      if (response.ok && data.success && data.clientAdmin) {
        const adminData = {
          id: data.clientAdmin.id,
          name: data.clientAdmin.name,
          applicationForm: data.clientAdmin.applicationForm,
        };
        
        setClientAdmin(adminData);
        // Store in sessionStorage - persists during page refresh but not browser close
        sessionStorage.setItem('clientAdmin', JSON.stringify(adminData));
        message.success('Successfully logged in as Client Admin');
        return { success: true };
      } else {
        const errorMessage = data.error || data.message || 'Login failed';
        message.error(errorMessage);
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      message.error('An error occurred during login. Please try again.');
      return { success: false, message: 'Network error' };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (name, password, applicationForm) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/clientadmin/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          password,
          applicationForm,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        message.success('Account created successfully! Please log in.');
        return { success: true };
      } else {
        const errorMessage = data.error || data.message || 'Registration failed';
        message.error(errorMessage);
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      message.error('An error occurred during registration. Please try again.');
      return { success: false, message: 'Network error' };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    setClientAdmin(null);
    // Remove from sessionStorage - this is the ONLY place we remove it
    sessionStorage.removeItem('clientAdmin');
  };

  const value = {
    clientAdmin,
    setClientAdmin, // Expose for manual restoration
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!clientAdmin,
  };

  return (
    <ClientAdminContext.Provider value={value}>
      {children}
    </ClientAdminContext.Provider>
  );
};
